import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  RadioGroup,
  Radio,
  FormControlLabel,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Stack,
  Box,
  Typography,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';

import { TYPE_OPTIONS, PRIORITY_OPTIONS } from './ticketConstants';
import { createDevTicket } from '../../collections/devTickets';

const IMAGE_UPLOAD_URL = 'https://api.maysfarm.vn/api/v1/images';

const uploadImage = async (file) => {
  const data = new FormData();
  data.append('file', file);
  const token = localStorage.getItem('token');
  const res = await axios.request({
    method: 'POST',
    url: IMAGE_UPLOAD_URL,
    headers: { Authorization: `Bearer ${token}` },
    data,
  });
  return res.data.data;
};

const CreateTicketDialog = ({ open, onClose, onCreated }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState('bug');
  const [priority, setPriority] = useState('medium');
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const reset = () => {
    setTitle('');
    setDescription('');
    setType('bug');
    setPriority('medium');
    setFiles([]);
    setPreviews([]);
    setError('');
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose();
  };

  const handleFiles = (event) => {
    const picked = Array.from(event.target.files || []);
    setFiles((prev) => [...prev, ...picked]);
    picked.forEach((f) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviews((prev) => [...prev, reader.result]);
      };
      reader.readAsDataURL(f);
    });
  };

  const removePreview = (idx) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
    setPreviews((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async () => {
    if (!title.trim() || !description.trim()) {
      setError('Tiêu đề và mô tả là bắt buộc.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const image_urls = [];
      for (const file of files) {
        const url = await uploadImage(file);
        if (url) image_urls.push(url);
      }
      const res = await createDevTicket({
        title: title.trim(),
        description: description.trim(),
        type,
        priority,
        image_urls,
      });
      onCreated(res.data);
      reset();
      onClose();
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.error || 'Tạo yêu cầu thất bại.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>
        Tạo yêu cầu mới
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <TextField
            label="Tiêu đề"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
            required
            autoFocus
          />
          <TextField
            label="Mô tả"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            required
            multiline
            rows={4}
          />
          <FormControl>
            <Typography variant="body2" sx={{ mb: 0.5 }}>Loại</Typography>
            <RadioGroup
              row
              value={type}
              onChange={(e) => setType(e.target.value)}
            >
              {TYPE_OPTIONS.map((opt) => (
                <FormControlLabel
                  key={opt.key}
                  value={opt.key}
                  control={<Radio />}
                  label={`${opt.emoji} ${opt.label}`}
                />
              ))}
            </RadioGroup>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel id="priority-label">Ưu tiên</InputLabel>
            <Select
              labelId="priority-label"
              value={priority}
              label="Ưu tiên"
              onChange={(e) => setPriority(e.target.value)}
            >
              {PRIORITY_OPTIONS.map((p) => (
                <MenuItem key={p.key} value={p.key}>{p.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box>
            <Button variant="outlined" component="label" size="small">
              Thêm ảnh chụp màn hình
              <input
                hidden
                type="file"
                accept="image/*"
                multiple
                onChange={handleFiles}
              />
            </Button>
            {previews.length > 0 && (
              <Stack direction="row" spacing={1} mt={1} flexWrap="wrap">
                {previews.map((src, i) => (
                  <Box key={i} sx={{ position: 'relative' }}>
                    <img
                      src={src}
                      alt={`preview-${i}`}
                      style={{
                        width: 80,
                        height: 80,
                        objectFit: 'cover',
                        borderRadius: 4,
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => removePreview(i)}
                      sx={{
                        position: 'absolute',
                        top: -8,
                        right: -8,
                        background: 'white',
                        boxShadow: 1,
                        '&:hover': { background: 'white' },
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Stack>
            )}
          </Box>
          {error && <Typography color="error" variant="body2">{error}</Typography>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>Hủy</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={submitting}
        >
          {submitting ? 'Đang tạo…' : 'Tạo'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateTicketDialog;
